import { useEffect, useState } from "react";
import { publishToArchive, setResearchConsent, setDemographics, getMyPrivacy } from "../api.js";
import { hasAny } from "../demographics.js";
import ConsentChoice, { EMPTY_WHO } from "../ui/ConsentChoice.jsx";

/**
 * 결과가 나온 뒤에 고를 것들 — 아카이브 공개, 연구·통계 동의.
 *
 * ★ 로그인 폼이 없다. 토론 자체가 로그인해야 되므로 여기 오는 사람은 늘 계정이
 * 있고, 그 토론은 이미 저장돼 있다. 전에는 이 시트가 통째로 로그인 폼에 싸여
 * 있어서 **로그인한 사람은 아카이브에 올릴 방법이 아예 없었다** —
 * publishToArchive 를 부르는 곳이 여기 하나뿐이기 때문이다.
 */
export default function Save({ debateId, onClose, isPublic = false }) {
  // ★ 지금 공개 중이면 체크된 상태로 연다. 체크를 풀고 저장하면 **내려간다.**
  // 약관 제6조가 「언제든지 공개를 취소할 수 있습니다」라고 약속하는데
  // 전에는 올리는 길만 있고 내리는 길이 없었다.
  const [pub, setPub] = useState(isPublic);
  const [research, setResearch] = useState(false);
  const [who, setWho] = useState(EMPTY_WHO);
  /**
   * ★ 이미 동의해 둔 사람에게 또 묻지 않는다.
   * 로그인·입장 화면에서 동의를 걷어냈더니 그 불편이 이리로 옮겨왔다 —
   * 매 토론마다 같은 체크박스를 다시 보면 「동의한 적 없나?」 하고 헷갈린다.
   * PeerCompare 는 서버가 주는 consented 로 이미 이렇게 한다. 같은 규칙을 쓴다.
   * 못 읽으면 물어본다 — 안 묻는 것보다 한 번 더 묻는 쪽이 안전하다.
   */
  const [already, setAlready] = useState(null); // null = 확인 중
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    getMyPrivacy()
      .then((p) => alive && setAlready(p))
      .catch(() => alive && setAlready({ researchConsent: false, demographics: null }));
    return () => { alive = false; };
  }, []);

  async function apply() {
    setBusy(true);
    setErr(null);
    try {
      if (pub !== isPublic) await publishToArchive(debateId, pub);
      if (research) {
        await setResearchConsent(true);
        // 동의 없이 보내지 않는다 — 체크를 풀면 고른 값도 안 나간다.
        if (hasAny(who)) await setDemographics(who);
      }
      setDone(true);
    } catch (e) {
      setErr(e?.message ?? "지금은 안 되네요. 잠시 뒤에 다시 해주세요.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Sheet onClose={onClose}>
        <h2 className="d" style={{ fontSize: 30, lineHeight: 1.2 }}>반영했어요</h2>
        <p className="lead">선택하신 대로 해뒀어요. 언제든 바꿀 수 있어요.</p>
        {pub
          ? <p className="note">아카이브에 올렸어요. 언제든 내릴 수 있어요.</p>
          : isPublic && <p className="note">아카이브에서 내렸어요.</p>}
        <button type="button" className="btn primary" onClick={onClose}>닫기</button>
      </Sheet>
    );
  }

  return (
    <Sheet onClose={onClose}>
      <h2 className="d" style={{ fontSize: 30, lineHeight: 1.2 }}>이 결과, 어떻게 할까요?</h2>
      <p className="lead">이미 계정에 저장돼 있어요. 다른 사람들에게 보여줄지만 정하시면 돼요.</p>

      <label className="card" style={{ flexDirection: "row", alignItems: "flex-start", gap: 12, padding: "16px 18px", borderRadius: 4 }}>
        <input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)}
          style={{ width: 22, height: 22, flexShrink: 0, marginTop: 1, accentColor: "#15151A" }} />
        <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ fontSize: 16, fontWeight: 800 }}>{isPublic ? "아카이브에 올려 둔 상태예요" : "아카이브에도 올릴게요"}</span>
          <span className="note">
            <strong>아카이브는 다른 사람들이 공개한 결과가 모이는 곳이에요.</strong>{" "}
            올리면 주제·확신도 변화·첫 카드·내가 쓴 문장 한 줄만 보여요.
            토론 원문은 안 올라가고, 언제든 내릴 수 있어요.
            {isPublic && <strong> 체크를 풀고 저장하면 내려가요.</strong>}
          </span>
        </span>
      </label>

      {already?.researchConsent
        ? (
          <p className="note" style={{ margin: 0 }}>
            연구·통계 이용에는 이미 동의해 두셨어요. 끄시려면 <strong>개인정보 설정</strong>에서 언제든 바꿀 수 있어요.
          </p>
        )
        : already && (
          <ConsentChoice agreed={research} onAgreed={setResearch} who={who} onWho={setWho} />
        )}

      {err && <p className="note" role="alert" style={{ margin: 0 }}>{err}</p>}

      <button type="button" className="btn primary" disabled={busy} onClick={apply}>
        {busy ? "반영하는 중…" : "이대로 할게요"}
      </button>
      <button type="button" className="btn" onClick={onClose}>나중에 할게요</button>
    </Sheet>
  );
}

/** 완료 화면과 선택 화면이 같은 껍데기를 쓴다. */
function Sheet({ children, onClose }) {
  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 10, background: "rgba(26,24,32,.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <section role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 600, boxSizing: "border-box",
          padding: "12px 18px calc(26px + env(safe-area-inset-bottom, 0px))",
          border: "1px solid var(--line)", borderBottom: 0, borderRadius: "32px 32px 0 0",
          background: "var(--paper)", display: "flex", flexDirection: "column", gap: 14,
          maxHeight: "86vh", overflowY: "auto",
        }}>
        <span aria-hidden="true" style={{ alignSelf: "center", width: 44, height: 5, borderRadius: 999, background: "var(--ink)" }} />
        {children}
      </section>
    </div>
  );
}
